<template>
  <div
    class="telegram-settings space-y-4"
    :class="{ 'telegram-settings--embed': embedInProfile }"
  >
    <div v-if="showTimezone" class="tz-card">
      <span class="telegram-section-label">Display timezone</span>
      <p class="text-xs text-slate-600 dark:text-zinc-400 mb-2 leading-snug">
        Dates and times in the app are stored in UTC and shown in this timezone.
      </p>
      <div class="flex items-center gap-2 flex-1 flex-wrap">
        <Select
          v-model="timezone"
          :options="timezoneOptions"
          option-label="label"
          option-value="value"
          filter
          filter-placeholder="Search timezone…"
          placeholder="Select timezone"
          class="flex-1 min-w-[220px]"
          :disabled="tzLoading"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          size="small"
          :loading="tzLoading"
          :disabled="tzLoading || !timezone"
          type="button"
          @click="saveTimezone"
        />
      </div>
    </div>

    <div
      class="telegram-panel"
      :class="{ 'telegram-panel--embed': embedInProfile }"
      :aria-busy="statusLoading || codeLoading || disconnectLoading"
    >
      <div
        class="telegram-panel__head"
        :class="{ 'telegram-panel__head--embed': embedInProfile }"
      >
        <div
          v-if="!embedInProfile"
          class="flex items-center gap-3 min-w-0 flex-wrap"
        >
          <span
            class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-400"
            aria-hidden="true"
          >
            <i class="pi pi-send text-lg"></i>
          </span>
          <div class="min-w-0">
            <div class="font-semibold text-slate-900 dark:text-zinc-100 text-sm sm:text-base">
              Telegram
            </div>
            <div class="text-xs text-slate-600 dark:text-zinc-300 mt-0.5 hidden sm:block">
              {{ statusOnly ? 'Linked Telegram account' : 'Private request and Attendance notifications.' }}
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2 flex-wrap w-full sm:w-auto sm:justify-end">
          <Tag v-if="!statusOnly && !statusKnown" :value="statusLoading ? 'Checking…' : 'Status unavailable'" severity="secondary" />
          <Tag v-else-if="!statusOnly && maintenanceMessage" value="Under reconstruction" severity="warn" />
          <Tag v-else-if="!statusOnly && botEnabled === false" value="Not available" severity="secondary" />
          <Tag v-else-if="displayConnected" value="Connected" severity="success" />
          <Tag v-else value="Not connected" severity="secondary" />
          <Button
            v-if="!statusOnly"
            label="Check status"
            icon="pi pi-refresh"
            text
            size="small"
            type="button"
            :loading="statusLoading"
            :disabled="codeLoading || disconnectLoading"
            @click="fetchTelegramStatus()"
          />
          <Button
            v-if="telegramStatus.connected && allowDisconnect"
            label="Disconnect"
            icon="pi pi-times"
            severity="danger"
            text
            size="small"
            type="button"
            :loading="disconnectLoading"
            :disabled="statusLoading || codeLoading || disconnectLoading"
            @click="confirmDisconnect"
          />
        </div>
      </div>

      <p v-if="telegramError" class="text-sm text-red-700 dark:text-red-300 my-3" role="alert">
        {{ telegramError }}
      </p>

      <template v-if="statusOnly">
        <p class="text-sm text-slate-600 dark:text-zinc-300 leading-snug">
          {{ statusOnlyConnected ? 'This employee has linked a Telegram account.' : 'Telegram is not linked for this employee.' }}
        </p>
      </template>

      <p v-else-if="!statusKnown" class="text-sm text-slate-600 dark:text-zinc-300" role="status">
        {{ statusLoading ? 'Checking your Telegram connection…' : 'Unable to check your connection. Use Check status to try again.' }}
      </p>

      <p v-else-if="maintenanceMessage" class="text-sm text-slate-600 dark:text-zinc-300">
        {{ maintenanceMessage }}
      </p>

      <p v-else-if="botEnabled === false" class="text-sm text-slate-600 dark:text-zinc-300">
        An active employee account or Attendance HR access is required to use the Telegram bot. Inbox remains available in the HR application.
      </p>

      <p v-else-if="!telegramStatus.connected && !botAvailable" class="text-sm text-slate-600 dark:text-zinc-300">
        Telegram connection is currently unavailable. Contact your administrator, then check the status again.
      </p>

      <template v-else-if="!telegramStatus.connected">
        <div
          class="telegram-connect-flow"
          :class="{ 'telegram-connect-flow--embed': embedInProfile }"
        >
          <div class="flex items-center justify-between gap-3 flex-wrap">
            <div class="min-w-0">
              <span class="telegram-step-label">1. Account email</span>
              <p v-if="accountEmail" class="text-sm font-medium text-slate-900 dark:text-zinc-100 break-all select-all">{{ accountEmail }}</p>
              <p v-else class="text-sm text-slate-600 dark:text-zinc-300">Use the email you sign in with.</p>
            </div>
            <Button
              v-if="accountEmail"
              label="Copy email"
              icon="pi pi-copy"
              text
              size="small"
              type="button"
              @click="copyValue(accountEmail, 'Account email')"
            />
          </div>
          <div class="telegram-panel__grid">
            <div class="telegram-open-bot min-w-0">
              <span class="telegram-step-label">2. Open bot</span>
              <a
                :href="botDeepLink"
                target="_blank"
                rel="noopener noreferrer"
                class="telegram-bot-link"
              >
                <i class="pi pi-telegram text-lg shrink-0" aria-hidden="true"></i>
                {{ botDisplayLabel }}
              </a>
              <p class="telegram-open-bot__hint">
                Press Start in Telegram, send your account email, then enter the code below when asked.
              </p>
            </div>
            <div v-if="!qrFailed" class="telegram-panel__qr shrink-0">
              <img
                :src="qrSrc"
                alt="QR code to open Telegram bot"
                class="telegram-qr-img"
                width="128"
                height="128"
                loading="lazy"
                @error="qrFailed = true"
              />
            </div>
          </div>

          <div class="telegram-code-box">
            <div class="min-w-0 flex-1">
              <span class="telegram-step-label">3. Verification code</span>
              <div
                v-if="codeIsActive"
                class="mt-2 font-mono text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 tracking-[0.25em] select-all"
              >
                {{ verificationCode }}
              </div>
              <p v-if="codeIsActive" class="mt-2 text-xs text-slate-600 dark:text-zinc-300">
                Expires in {{ codeTimeRemaining }}. Refreshing the code replaces the previous one.
              </p>
              <p v-else-if="verificationCode" class="mt-2 text-sm text-amber-700 dark:text-amber-300" role="status">
                This code expired. Generate a new code to continue.
              </p>
              <p
                v-else
                class="mt-2 text-sm text-slate-600 dark:text-zinc-300"
              >
                Generate a code, then enter it in the bot within 10 minutes.
              </p>
            </div>
            <div class="flex gap-2 flex-wrap shrink-0">
              <Button
                v-if="codeIsActive"
                label="Copy code"
                icon="pi pi-copy"
                text
                size="small"
                type="button"
                :disabled="codeLoading"
                @click="copyValue(verificationCode, 'Verification code')"
              />
              <Button
                :label="codeIsActive ? 'Refresh code' : verificationCode ? 'Generate new code' : 'Generate code'"
                :icon="verificationCode ? 'pi pi-refresh' : 'pi pi-link'"
                severity="primary"
                size="small"
                type="button"
                class="telegram-code-action shrink-0"
                :loading="codeLoading"
                :disabled="statusLoading || codeLoading || disconnectLoading"
                @click="generateCode"
              />
            </div>
          </div>

          <p
            v-if="codeIsActive"
            class="telegram-code-hint text-xs text-slate-600 dark:text-zinc-300 leading-relaxed"
          >
            This page checks automatically while the code is active. After the bot confirms the connection, you can also use Check status.
          </p>
        </div>
      </template>

      <template v-else>
        <div class="telegram-connected-summary space-y-2 mt-4 pt-4 border-t border-slate-200/90 dark:border-zinc-700/80">
          <span class="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-400">
            Your Telegram profile
          </span>
          <a
            v-if="telegramProfileHref"
            :href="telegramProfileHref"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 text-sky-600 hover:text-sky-500 dark:text-sky-400 font-medium text-sm transition-colors break-all"
          >
            <i class="pi pi-telegram shrink-0 text-lg" aria-hidden="true"></i>
            {{ telegramProfileLinkLabel }}
          </a>
          <p v-else class="text-xs text-slate-600 dark:text-zinc-400 leading-snug">
            Connected. Use Telegram on your device to open this chat with the bot.
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { useSettingsDraftGuard } from '@/features/settings/composables/useSettingsDraftGuard';
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Select from 'primevue/select';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { timezoneOptions } from './timezoneOptions.js';
import { getUserTimezone, persistUserTimezone } from '@/utils/userTimezone';
import { normalizeTelegramUsername, telegramBotLink, telegramProfileLink, telegramCodeExpiry, telegramErrorMessage } from './telegramAccountUi';

defineOptions({ name: 'TelegramUserSettings' });

const emit = defineEmits(['status-change']);

const props = defineProps({
  // axios.create() returns a callable function (instance), not a plain object
  httpClient: {
    type: [Object, Function],
    required: true,
  },
  telegramAccountPath: {
    type: String,
    default: 'telegram/account',
  },
  showTimezone: {
    type: Boolean,
    default: true,
  },
  fallbackTelegramUrl: {
    type: String,
    default: '',
  },
  allowDisconnect: {
    type: Boolean,
    default: true,
  },
  statusOnly: {
    type: Boolean,
    default: false,
  },
  /** Profile overview card: outer shell owns title; compact connect layout. */
  embedInProfile: {
    type: Boolean,
    default: false,
  },
  externalConnected: {
    type: Boolean,
    default: null,
  },
});

const confirm = useConfirm();
const toast = useToast();
const store = useStore();

/** Axios interceptor rejects with `{ message, response }` — not a raw AxiosError. */
function detailFromErr(err) {
  return telegramErrorMessage(err, '');
}

const timezone = ref('UTC');
const tzLoading = ref(false);

const telegramStatus = ref({
  connected: false,
  verified_at: null,
  telegram_username: null,
  telegram_profile_url: null,
});
const statusKnown = ref(false);
const statusLoading = ref(true);
const telegramError = ref('');
const botConfigured = ref(null);
const botEnabled = ref(null);
const maintenanceMessage = ref('');
const serverAccountEmail = ref('');
const verificationCode = ref('');
const codeExpiresAt = ref(0);
const currentTime = ref(Date.now());
const botUsername = ref('');
const qrFailed = ref(false);
const codeLoading = ref(false);
const disconnectLoading = ref(false);
let alive = true;
let requestEpoch = 0;
let telegramEpoch = 0;
let statusRequestId = 0;
let statusPromise = null;
let clockTimer;
let pollTimer;
const pendingRequests = new Map();
const instanceId = Symbol('telegram-settings');
const accountEmail = computed(() => serverAccountEmail.value || store.state.users?.user?.email || '');
const codeIsActive = computed(() => !!verificationCode.value && codeExpiresAt.value > currentTime.value);
const codeTimeRemaining = computed(() => {
  const seconds = Math.max(0, Math.ceil((codeExpiresAt.value - currentTime.value) / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
});

const statusOnlyConnected = computed(() => {
  if (props.externalConnected !== null) {
    return !!props.externalConnected;
  }
  return false;
});

const displayConnected = computed(() => {
  if (props.statusOnly) {
    return statusOnlyConnected.value;
  }
  return !!telegramStatus.value.connected;
});

const botDeepLink = computed(() => {
  if (botConfigured.value === false || botEnabled.value === false || maintenanceMessage.value) return '';
  return telegramBotLink(botUsername.value, props.fallbackTelegramUrl);
});
const botAvailable = computed(() => !!botDeepLink.value);

const botDisplayLabel = computed(() => {
  if (botUsername.value) {
    return `@${botUsername.value}`;
  }
  if (botDeepLink.value) return `@${botDeepLink.value.split('/').pop()}`;
  return 'Telegram bot';
});

const qrSrc = computed(() => {
  const u = encodeURIComponent(botDeepLink.value);
  return `https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${u}&format=png&bgcolor=ffffff&color=000000&margin=4`;
});

const telegramProfileHref = computed(() => telegramProfileLink(telegramStatus.value?.telegram_profile_url));

const telegramProfileLinkLabel = computed(() => {
  const u = telegramStatus.value?.telegram_username;
  if (u) {
    return `@${u}`;
  }
  return 'Open your Telegram profile';
});

async function postAccount(body) {
  const controller = new AbortController();
  pendingRequests.set(controller, body.action);
  try {
    return await props.httpClient.post(props.telegramAccountPath, body, {
      signal: controller.signal,
      timeout: 15000,
    });
  } finally {
    pendingRequests.delete(controller);
  }
}

const timezoneDraftGuard = useSettingsDraftGuard({
  draft: () => ({ timezone: timezone.value }),
  visible: () => props.showTimezone,
  saving: () => tzLoading.value || codeLoading.value || disconnectLoading.value,
});

const fetchTimezone = async () => {
  const epoch = requestEpoch;
  if (!props.showTimezone) {
    return;
  }
  const fromStore = store.state.users?.user?.timezone;
  if (fromStore) {
    timezone.value = fromStore;
    timezoneDraftGuard.markSaved();
    return;
  }
  try {
    const res = await postAccount({ action: 'getTimezone' });
    if (!alive || epoch !== requestEpoch) return;
    if (res.data?.success) {
      const tz = res.data.data?.timezone || res.data.timezone || 'UTC';
      timezone.value = tz;
      timezoneDraftGuard.markSaved();
      store.commit('users/setMeData', { timezone: tz });
    }
  } catch {
    if (!alive || epoch !== requestEpoch) return;
    timezone.value = getUserTimezone();
    timezoneDraftGuard.markSaved();
  }
};

const saveTimezone = async () => {
  if (tzLoading.value || !timezone.value) return;
  const epoch = requestEpoch;
  const requestedTimezone = timezone.value;
  tzLoading.value = true;
  try {
    const res = await postAccount({ action: 'updateTimezone', timezone: requestedTimezone });
    if (!alive || epoch !== requestEpoch) return;
    if (res.data?.success) {
      const tz = res.data.data?.timezone || res.data.timezone || requestedTimezone;
      timezone.value = tz;
      timezoneDraftGuard.markSaved();
      persistUserTimezone(tz);
      store.commit('users/setMeData', { timezone: tz });
      toast.add({ severity: 'success', summary: 'Saved', detail: 'Timezone updated', life: 3000 });
    } else {
      toast.add({ severity: 'error', summary: 'Error', detail: res.data?.message || '', life: 3000 });
    }
  } catch (err) {
    if (!alive || epoch !== requestEpoch) return;
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: detailFromErr(err) || 'Failed to save timezone',
      life: 3000,
    });
  } finally {
    if (alive && epoch === requestEpoch) tzLoading.value = false;
  }
};

function emitStatusChange() {
  emit('status-change', {
    connected: !!telegramStatus.value.connected,
    telegram_username: telegramStatus.value.telegram_username ?? null,
    telegram_profile_url: telegramStatus.value.telegram_profile_url ?? null,
  });
}

function clearCode() {
  verificationCode.value = '';
  codeExpiresAt.value = 0;
}

function broadcastAccountChange() {
  window.dispatchEvent(new CustomEvent('hr:telegram-account-change', { detail: { source: instanceId } }));
}

function updateBotDetails(data) {
  botUsername.value = normalizeTelegramUsername(data.bot_username);
  if (typeof data.bot_configured === 'boolean') botConfigured.value = data.bot_configured;
  if (typeof data.bot_enabled === 'boolean') {
    botEnabled.value = data.bot_enabled;
    if (!data.bot_enabled) clearCode();
  }
  maintenanceMessage.value = data.maintenance === true && typeof data.maintenance_message === 'string'
    ? data.maintenance_message
    : '';
  if (maintenanceMessage.value) clearCode();
  if (typeof data.account_email === 'string') serverAccountEmail.value = data.account_email;
}

function fetchTelegramStatus() {
  if (!alive || props.statusOnly || codeLoading.value || disconnectLoading.value) return Promise.resolve();
  if (statusPromise) return statusPromise;
  const epoch = requestEpoch;
  const telegramVersion = telegramEpoch;
  const requestId = ++statusRequestId;
  statusLoading.value = true;
  statusPromise = (async () => {
    try {
      const res = await postAccount({ action: 'getStatus' });
      if (!isCurrentTelegramRequest(epoch, telegramVersion) || requestId !== statusRequestId) return;
      if (!res.data?.success || !res.data.data) {
        throw new Error(res.data?.message || 'Unable to check your Telegram connection. Please try again.');
      }
      const wasConnected = !!telegramStatus.value.connected;
      const wasKnown = statusKnown.value;
      const hadCode = !!verificationCode.value;
      telegramStatus.value = res.data.data;
      statusKnown.value = true;
      telegramError.value = '';
      updateBotDetails(res.data.data);
      if (telegramStatus.value.connected) clearCode();
      emitStatusChange();
      if (!wasConnected && telegramStatus.value.connected && hadCode && botEnabled.value !== false) {
        toast.add({ severity: 'success', summary: 'Connected', detail: 'Telegram notifications are ready.', life: 4000 });
      }
      if (wasKnown && wasConnected !== !!telegramStatus.value.connected) broadcastAccountChange();
    } catch (err) {
      if (!isCurrentTelegramRequest(epoch, telegramVersion) || requestId !== statusRequestId) return;
      telegramError.value = detailFromErr(err) || 'Unable to check your Telegram connection. Please try again.';
    } finally {
      if (isCurrentTelegramRequest(epoch, telegramVersion) && requestId === statusRequestId) {
        statusLoading.value = false;
        statusPromise = null;
      }
    }
  })();
  return statusPromise;
}

const generateCode = async () => {
  if (!alive || props.statusOnly || !statusKnown.value || !botAvailable.value || telegramStatus.value.connected
    || codeLoading.value || disconnectLoading.value || statusLoading.value) return;
  const epoch = requestEpoch;
  const telegramVersion = telegramEpoch;
  ++statusRequestId;
  clearCode();
  codeLoading.value = true;
  telegramError.value = '';
  let refreshAfterConflict = false;
  try {
    const res = await postAccount({ action: 'generateCode' });
    if (!isCurrentTelegramRequest(epoch, telegramVersion)) return;
    if (res.data?.success && /^[A-Z0-9]{6}$/.test(res.data.data?.code || '')) {
      verificationCode.value = res.data.data.code;
      currentTime.value = Date.now();
      codeExpiresAt.value = telegramCodeExpiry(res.data.data, currentTime.value);
      updateBotDetails(res.data.data);
      broadcastAccountChange();
    } else {
      telegramError.value = res.data?.message || 'Failed to generate code. Please try again.';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: telegramError.value,
        life: 4000,
      });
    }
  } catch (err) {
    if (!isCurrentTelegramRequest(epoch, telegramVersion)) return;
    telegramError.value = detailFromErr(err) || 'Failed to generate code. Please try again.';
    if (err?.response?.status === 409) {
      // The connection may have completed while the user clicked Generate code.
      refreshAfterConflict = true;
    }
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: telegramError.value,
      life: 6000,
    });
  } finally {
    if (isCurrentTelegramRequest(epoch, telegramVersion)) {
      codeLoading.value = false;
      if (refreshAfterConflict) await fetchTelegramStatus();
    }
  }
};

const doDisconnect = async () => {
  if (!alive || props.statusOnly || !props.allowDisconnect || disconnectLoading.value || codeLoading.value) return;
  const epoch = requestEpoch;
  const telegramVersion = telegramEpoch;
  ++statusRequestId;
  statusPromise = null;
  statusLoading.value = false;
  disconnectLoading.value = true;
  telegramError.value = '';
  try {
    const res = await postAccount({ action: 'disconnect' });
    if (!isCurrentTelegramRequest(epoch, telegramVersion)) return;
    if (res.data?.success) {
      telegramStatus.value = {
        connected: false,
        verified_at: null,
        telegram_username: null,
        telegram_profile_url: null,
      };
      clearCode();
      emitStatusChange();
      broadcastAccountChange();
      toast.add({ severity: 'success', summary: 'Disconnected', detail: 'Telegram unlinked', life: 3000 });
    } else {
      telegramError.value = res.data?.message || 'Failed to disconnect Telegram. Please try again.';
      toast.add({ severity: 'error', summary: 'Error', detail: telegramError.value, life: 4000 });
    }
  } catch (err) {
    if (!isCurrentTelegramRequest(epoch, telegramVersion)) return;
    telegramError.value = detailFromErr(err) || 'Failed to disconnect Telegram. Please try again.';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: telegramError.value,
      life: 4000,
    });
  } finally {
    if (isCurrentTelegramRequest(epoch, telegramVersion)) disconnectLoading.value = false;
  }
};

const confirmDisconnect = () => {
  if (!props.allowDisconnect || disconnectLoading.value || codeLoading.value) return;
  const epoch = requestEpoch;
  const telegramVersion = telegramEpoch;
  confirm.require({
    message: 'Disconnect Telegram? You will stop receiving Telegram notifications.',
    header: 'Disconnect Telegram',
    icon: 'pi pi-exclamation-triangle',
    acceptProps: { severity: 'danger' },
    rejectProps: { severity: 'secondary', variant: 'outlined' },
    rejectLabel: 'Cancel',
    acceptLabel: 'Disconnect',
    accept: () => {
      if (isCurrentTelegramRequest(epoch, telegramVersion)) return doDisconnect();
    },
  });
};

async function copyValue(value, label) {
  if (!value) return;
  const epoch = requestEpoch;
  try {
    await navigator.clipboard.writeText(value);
    if (!alive || epoch !== requestEpoch) return;
    toast.add({ severity: 'success', summary: 'Copied', detail: `${label} copied.`, life: 2500 });
  } catch {
    if (!alive || epoch !== requestEpoch) return;
    toast.add({ severity: 'warn', summary: 'Copy unavailable', detail: 'Select the text and copy it manually.', life: 4000 });
  }
}

function refreshOnReturn() {
  if (document.visibilityState === 'visible') {
    currentTime.value = Date.now();
    fetchTelegramStatus();
  }
}

function refreshFromAnotherScreen(event) {
  if (event.detail?.source === instanceId) return;
  invalidateTelegramRequests();
  clearCode();
  fetchTelegramStatus();
}

function isCurrentTelegramRequest(epoch, telegramVersion) {
  return alive && epoch === requestEpoch && telegramVersion === telegramEpoch;
}

function invalidateTelegramRequests() {
  ++telegramEpoch;
  ++statusRequestId;
  statusPromise = null;
  pendingRequests.forEach((action, controller) => {
    if (['getStatus', 'generateCode', 'disconnect'].includes(action)) controller.abort();
  });
  statusLoading.value = false;
  codeLoading.value = false;
  disconnectLoading.value = false;
}

function invalidateRequests() {
  ++requestEpoch;
  invalidateTelegramRequests();
  pendingRequests.forEach((_action, controller) => controller.abort());
}

watch(() => [props.statusOnly, props.telegramAccountPath, props.httpClient, store.state.users?.user?.id], () => {
  invalidateRequests();
  clearCode();
  statusKnown.value = false;
  statusLoading.value = !props.statusOnly;
  telegramError.value = '';
  codeLoading.value = false;
  disconnectLoading.value = false;
  tzLoading.value = false;
  botConfigured.value = null;
  botEnabled.value = null;
  maintenanceMessage.value = '';
  botUsername.value = '';
  serverAccountEmail.value = '';
  telegramStatus.value = { connected: false };
  if (!props.statusOnly) fetchTelegramStatus();
  fetchTimezone();
});

watch(botDeepLink, () => { qrFailed.value = false; });

onMounted(() => {
  if (!props.statusOnly) {
    fetchTelegramStatus();
  }
  fetchTimezone();
  clockTimer = setInterval(() => { currentTime.value = Date.now(); }, 1000);
  pollTimer = setInterval(() => {
    if (codeIsActive.value && document.visibilityState === 'visible') fetchTelegramStatus();
  }, 5000);
  window.addEventListener('focus', refreshOnReturn);
  document.addEventListener('visibilitychange', refreshOnReturn);
  window.addEventListener('hr:telegram-account-change', refreshFromAnotherScreen);
});

onBeforeUnmount(() => {
  alive = false;
  invalidateRequests();
  clearInterval(clockTimer);
  clearInterval(pollTimer);
  window.removeEventListener('focus', refreshOnReturn);
  document.removeEventListener('visibilitychange', refreshOnReturn);
  window.removeEventListener('hr:telegram-account-change', refreshFromAnotherScreen);
});
</script>

<style scoped>
.tz-card {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--p-surface-card, var(--surface-card));
  border: 1px solid var(--p-surface-border, var(--surface-border));
  border-radius: 0.75rem;
}

.telegram-section-label {
  font-weight: 600;
  min-width: 100px;
  font-size: 0.875rem;
  color: var(--p-text-muted-color, var(--text-muted-color));
}

.telegram-panel {
  border-radius: 0.75rem;
  border: 1px solid rgb(226 232 240 / 0.95);
  background: rgb(255 255 255 / 0.92);
  padding: 1rem 1.25rem 1.25rem;
}

.dark-mode .telegram-panel {
  border-color: rgb(63 63 70 / 0.95);
  background: rgb(39 39 42 / 0.75);
  box-shadow: inset 0 1px 0 0 rgb(63 63 70 / 0.25);
}

.telegram-panel__head {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgb(241 245 249 / 0.95);
}

@media (min-width: 640px) {
  .telegram-panel__head {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.dark-mode .telegram-panel__head {
  border-bottom-color: rgb(63 63 70 / 0.85);
}

.telegram-panel__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  align-items: start;
}

@media (min-width: 768px) {
  .telegram-panel__grid {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .telegram-panel__qr {
    justify-self: end;
  }
}

.telegram-code-box {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  border: 1px dashed rgb(203 213 225 / 0.95);
  background: rgb(248 250 252 / 0.9);
}

@media (min-width: 640px) {
  .telegram-code-box {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
}

.dark-mode .telegram-code-box {
  border-color: rgb(82 82 91 / 0.9);
  background: rgb(24 24 27 / 0.65);
}

/* Prime primary button contrast on dark dashed block */
.dark-mode .telegram-code-action.p-button-primary {
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.35);
}

.telegram-step-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(100 116 139);
  margin-bottom: 0.375rem;
}

.dark-mode .telegram-step-label {
  color: rgb(161 161 170);
}

.telegram-bot-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgb(2 132 199);
  transition: color 0.15s ease;
  word-break: break-all;
}

.telegram-bot-link:hover {
  color: rgb(14 165 233);
}

.dark-mode .telegram-bot-link {
  color: rgb(56 189 248);
}

.dark-mode .telegram-bot-link:hover {
  color: rgb(125 211 252);
}

.telegram-open-bot__hint {
  margin: 0.5rem 0 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: rgb(100 116 139);
}

.dark-mode .telegram-open-bot__hint {
  color: rgb(212 212 216);
}

.telegram-qr-img {
  border-radius: 0.75rem;
  border: 1px solid rgb(226 232 240);
  background: #fff;
  padding: 0.375rem;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.06);
}

.dark-mode .telegram-qr-img {
  border-color: rgb(82 82 91);
  box-shadow: inset 0 0 0 1px rgb(63 63 70 / 0.5);
}

.telegram-connect-flow--embed {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.telegram-connect-flow--embed .telegram-panel__grid {
  gap: 1rem 1.25rem;
}

.telegram-connect-flow--embed .telegram-code-box {
  margin-top: 0;
  width: 100%;
}

@media (min-width: 480px) {
  .telegram-connect-flow--embed .telegram-panel__grid {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }

  .telegram-connect-flow--embed .telegram-panel__qr {
    justify-self: end;
  }
}

.telegram-panel--embed {
  padding: 1rem 1.25rem 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid rgb(226 232 240 / 0.95);
  background: rgb(248 250 252 / 0.65);
  box-shadow: none;
}

.dark-mode .telegram-panel.telegram-panel--embed {
  border-color: rgb(63 63 70 / 0.95);
  background: rgb(39 39 42 / 0.75);
  box-shadow: inset 0 1px 0 0 rgb(63 63 70 / 0.25);
}

.telegram-panel__head--embed {
  margin: 0;
  padding: 0;
  border: 0;
  flex-direction: row;
  width: auto;
}

.telegram-settings--embed .telegram-connected-summary {
  margin-top: 0;
  padding-top: 0;
  border-top: 0;
}
</style>
